<template>
    <div class="datePicker">
        <div class="datePicker__wrap">
            <div v-if="icon" class="datePicker__icon">
                <fa-icon :icon="icon" />
            </div>

            <span>
                <date-picker
                    v-if="type === 'range'"
                    v-model.range="range"
                    :mode="typePicker"
                    :popover="{
                        visibility: 'focus',
                        positionFixed: true,
                    }"
                    :masks="{
                        input: [format],
                        inputDateTime: [formatTime],
                    }"
                    :locale="'en-US'"
                    :is24hr="isTime"
                    :min-date="minDate"
                    @dayclick="
                        (_, event) => {
                            event.target.blur();
                        }
                    "
                    @update:model-value="updateCalendar"
                >
                    <template #default="{ inputValue, inputEvents }">
                        <template v-if="isCustomInput">
                            <slot
                                name="customInput"
                                :inputValue="inputValue"
                                :inputEvents="inputEvents"
                            />
                        </template>
                        <template v-else>
                            <input
                                class="vc-text-base"
                                :placeholder="placeholder"
                                :value="hasValue ? `${inputValue.start} - ${inputValue.end}` : ''"
                                :disabled="disabled"
                                @input="handleInputEvent"
                                v-on="inputEvents.start"
                            />
                        </template>
                    </template>
                </date-picker>
                <date-picker
                    v-if="type === 'single'"
                    v-model="single.date"
                    v-model.update="updateCalendar"
                    :mode="typePicker"
                    :popover="{
                        visibility: 'focus',
                        positionFixed: true,
                    }"
                    :masks="{
                        input: [format],
                        inputDateTime: [formatTime],
                    }"
                    :locale="'en-US'"
                    :is24hr="isTime"
                    :attributes="attr"
                    :min-date="minDate"
                    @dayclick="
                        (_, event) => {
                            event.target.blur();
                        }
                    "
                >
                    <template #default="{ inputValue, inputEvents }">
                        <template v-if="isCustomInput">
                            <slot
                                name="customInput"
                                :placeholder="placeholder"
                                :inputValue="inputValue"
                                :inputEvents="inputEvents"
                            />
                        </template>
                        <template v-else>
                            <input
                                class="vc-text-base"
                                :placeholder="placeholder"
                                :value="inputValue"
                                :disabled="disabled"
                                @input="handleInputEvent"
                                v-on="inputEvents"
                            />
                        </template>
                    </template>
                </date-picker>
            </span>
        </div>
        <p v-if="error" class="help has-text-danger" v-text="error" />
    </div>
</template>

<script>
import { DatePicker } from 'v-calendar';
import 'v-calendar/style.css';
import moment from 'moment';

export default {
    emits: ['updateCalendar', 'input'],
    
    components: {
        DatePicker,
    },

    props: {
        date: { type: [String, Object] },
        icon: { type: [Array, String] },
        type: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        checkEmpty: { type: Boolean, default: false },
        isTime: { type: Boolean, default: false },
        isCustomInput: { type: Boolean, default: false },
        attrData: { type: Array, default: () => [] },
        disabled: { type: Boolean, default: false },
        format: { type: String, default: 'MMM, D YYYY' },
        formatTime: { type: String, default: 'MMM, D YYYY - HH:mm' },
        minDate: { type: [String, Date, null], default: null },
        error: { type: String, default: '' },
    },

    data() {
        return {
            range: {
                start: moment().toDate(),
                end: moment().toDate(),
            },
            single: {
                date: moment().toDate(),
            },
            timezone: '',
            attr: [],
        };
    },

    computed: {
        typePicker() {
            if (this.isTime) return 'dateTime';

            if (this.type === 'range') {
                return 'date';
            }

            return 'date';
        },

        hasValue() {
            if (this.type === 'range') {
                return Boolean(this.range?.start && this.range?.end);
            }

            return Boolean(this.single.date);
        },

        value() {
            if (this.type === 'range') {
                return this.range;
            }

            return this.single.date;
        },
    },

    watch: {
        date(value) {
            this.init();
        },

        type() {
            this.init();
        },
    },

    methods: {
        init() {
            if (this.type === 'range') {
                if (this.date) {
                    this.range = {
                        start: moment(this.date.from).toDate(),
                        end: moment(this.date.to).toDate(),
                    };
                } else {
                    this.range = {
                        start: moment().toDate(),
                        end: moment().toDate(),
                    };
                }
            }

            if (this.type === 'single') {
                if (this.date) {
                    this.single.date = moment(this.date).toDate();
                } else {
                    this.single.date = moment().toDate();
                }
            }
        },

        updateCalendar(values) {
            if (this.checkEmpty) {
                if (!values) return;
            } else {
                if (!values) {
                    if (this.type === 'range') {
                        values = {
                            end: '',
                            start: '',
                        };
                    }

                    if (this.type === 'single') {
                        values = '';
                    }
                }

                if (this.type === 'range') {
                    if (this.date.from === values.end && this.date.to === values.end) return;
                }

                if (this.type === 'single') {
                    if (this.date === values) return;
                }
            }

            this.$emit('updateCalendar', {
                type: this.type,
                data: values,
            });
        },

        handleInputEvent(e) {
            this.$emit('input', e.target.value);
        },
    },

    mounted() {
        this.init();
    },
};
</script>
